import React from 'react';
import clsx from 'clsx';
import { BoltIcon } from '@heroicons/react/20/solid';
import { InspoStory } from '../../../services';
import { StatTiles } from './StoryParts';

// #region coverOf
/** The founder's face first, then the brand's own photo, then their #1 design. */
const coverOf = (s: InspoStory): string | null => {
    const best = s.best_sellers.find((b) => b.image_url || b.mockup_thumb_url);
    return s.portrait_url ?? s.photo_urls[0] ?? best?.image_url ?? best?.mockup_thumb_url ?? null;
};

// #region StoryPicker
/** Meet the founders: big portrait cards. Picking one swaps it into the story above. */
export const StoryPicker: React.FunctionComponent<{ stories: InspoStory[]; activeKey: string | null; onPick: (key: string) => void }> = ({ stories, activeKey, onPick }) => {
    if (stories.length < 2) return null;
    return (
        <section className="space-y-4">
            <h2 className="font-display text-3xl font-semibold tracking-tight text-black dark:text-white">Meet the founders</h2>
            <div role="tablist" aria-label="Founder stories" className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
                {stories.map((s) => {
                    const active = s.key === activeKey;
                    const cover = coverOf(s);
                    return (
                        <button key={s.key} type="button" role="tab" aria-selected={active} onClick={() => onPick(s.key)}
                            className={clsx('group relative overflow-hidden rounded-2xl text-left transition',
                                active ? 'ring-4 ring-blue-600 ring-offset-2 ring-offset-surface-sunken' : 'hover:-translate-y-0.5 hover:shadow-lg')}>
                            {cover && <img src={cover} alt={s.founder_name ?? s.name} className="aspect-[4/5] w-full object-cover object-top transition duration-300 group-hover:scale-[1.03]" />}
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent p-3 pt-12 sm:p-4 sm:pt-16">
                                <p className="font-display text-lg font-semibold leading-tight text-white sm:text-2xl">{s.founder_name ?? s.name}</p>
                                <p className="truncate text-xs text-white/80 sm:text-sm">{s.founder_name ? s.name : s.started_label}</p>
                                <p className="mt-1 inline-block rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-blue-600">{s.figures.monthly_now} a month</p>
                            </div>
                            {active && <span className="absolute left-3 top-3 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">Reading</span>}
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

// #region RisingCard
/** The newest fast starter, on the wallmates.com coral card: proof a brand-new sign-up can move quickly today. */
export const RisingCard: React.FunctionComponent<{ story: InspoStory }> = ({ story }) => (
    <article className="flex h-full flex-col gap-4 rounded-2xl bg-brand p-6 text-white">
        <span className="inline-flex w-fit items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold">
            <BoltIcon className="h-3.5 w-3.5" aria-hidden="true" /> Rising · new on Wallmates
        </span>
        <p className="font-display text-3xl font-semibold leading-tight">{story.headline}</p>
        <p className="text-sm text-white/85">{story.name}{story.started_label ? ` · ${story.started_label.toLowerCase()}` : ''}</p>
        <div className="mt-auto"><StatTiles story={story} withMonthly /></div>
    </article>
);
